"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Package, Calendar } from "lucide-react";
import toast from "react-hot-toast";
import api from "../services/api"; // Assuming this path is correct relative to src/components

interface Plugin {
  _id: string;
  fileName: string;
  platform: string;
  version: string;
  bit: string;
  fileSize: number;
  uploadedDate: string; // This will be a string in ISO format from the API
  status: string;
  downloadCount: number;
  downloadURL: string; // Direct download URL provided by the API
}

export default function PluginPage() {
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlugins();
  }, []);

  const fetchPlugins = async () => {
    try {
      setLoading(true);
      // Adjusting the endpoint to match your Node.js server's route for plugins
      const response = await api.get("/plugins");
      if (response.data.success) {
        // Accessing the 'plugins' array from the response data
        setPlugins(response.data.plugins);
      }
    } catch (error: any) {
      console.error("Error fetching plugins:", error);
      toast.error("Failed to fetch plugins");
    } finally {
      setLoading(false);
    }
  };

  // Function to format date to India Timestamp (DD/MM/YYYY)
  const formatIndianDate = (dateString: string) => {
    const date = new Date(dateString);
    // Using 'en-IN' locale for Indian date format (DD/MM/YYYY)
    return date.toLocaleDateString("en-IN");
  };

  const handleDownload = (downloadURL: string, fileName: string) => {
    // Use the downloadURL provided directly by the API
    window.open(downloadURL, "_blank"); // Opens in a new tab to trigger download
    toast.success(`Downloading ${fileName}...`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Plugin Management
          </h1>
          <p className="text-gray-600">
            Browse and download available system plugins.
          </p>
        </div>
      </motion.div>

      {/* Plugins List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        {plugins.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No plugins available</p>
            <p className="text-gray-400">
              Check back later for new plugins or contact support.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Platform
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    File Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Version
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Uploaded Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plugins.map((plugin, index) => (
                  <motion.tr
                    key={plugin._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-5 h-5 text-gray-500 mr-2" />
                        <div className="text-sm font-medium text-gray-900">
                          {plugin.platform}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {plugin.fileName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {plugin.version}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar size={16} className="mr-1" />
                        {formatIndianDate(plugin.uploadedDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() =>
                          handleDownload(plugin.downloadURL, plugin.fileName)
                        }
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title={`Download ${plugin.fileName}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}
